import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Database, Download, Trash2, RefreshCw, Shield, Clock, BarChart3, Loader2, FileText, Activity } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import AdminDsarTab from '@/components/admin/AdminDsarTab';
import AdminDataQualityTab from '@/components/admin/AdminDataQualityTab';
import { toast } from '@/hooks/use-toast';
import { logDataProcessing } from '@/lib/compliance-logger';

interface TableStat {
  table_name: string;
  row_count: number;
  total_size: string;
  size_bytes: number;
}

const RETENTION_POLICIES = [
  { table: 'medical_notes', retention: '7 years', legal: 'HIPAA', autoCleanup: false, category: 'clinical' },
  { table: 'session_transcripts', retention: '7 years', legal: 'HIPAA', autoCleanup: false, category: 'clinical' },
  { table: 'bookings', retention: '7 years', legal: 'HIPAA + Tax', autoCleanup: false, category: 'clinical' },
  { table: 'messages', retention: '7 years', legal: 'HIPAA', autoCleanup: false, category: 'clinical' },
  { table: 'conversations', retention: '7 years', legal: 'HIPAA', autoCleanup: false, category: 'clinical' },
  { table: 'reviews', retention: '7 years', legal: 'HIPAA', autoCleanup: false, category: 'clinical' },
  { table: 'clients', retention: 'Permanent', legal: 'Account Data', autoCleanup: false, category: 'account' },
  { table: 'practitioners', retention: 'Permanent', legal: 'Account Data', autoCleanup: false, category: 'account' },
  { table: 'services', retention: 'Permanent', legal: 'Business Data', autoCleanup: false, category: 'account' },
  { table: 'analytics_events', retention: '90 days', legal: 'None', autoCleanup: true, category: 'operational' },
  { table: 'email_logs', retention: '180 days', legal: 'None', autoCleanup: true, category: 'operational' },
  { table: 'user_sessions', retention: '30 days', legal: 'None', autoCleanup: true, category: 'operational' },
  { table: 'system_message_dismissals', retention: '90 days', legal: 'None', autoCleanup: true, category: 'operational' },
  { table: 'ai_navigator_conversations', retention: 'User-managed', legal: 'GDPR right to delete', autoCleanup: false, category: 'user-managed' },
];

const EXPORTABLE_TABLES = [
  'analytics_events', 'bookings', 'booking_status_history', 'clients', 'practitioners',
  'email_logs', 'email_campaigns', 'email_recipients', 'messages', 'reviews',
  'services', 'session_transcripts', 'user_sessions', 'ai_navigator_conversations',
  'ai_token_usage', 'job_openings', 'job_applications', 'group_events',
  'group_event_registrations',
];

const AdminDataManagement = () => {
  const { t } = useTranslation('admin');
  const [tableStats, setTableStats] = useState<TableStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult] = useState<Record<string, number> | null>(null);
  const [exportTable, setExportTable] = useState('');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchTableStats();
  }, []);

  const fetchTableStats = async () => {
    setLoading(true);
    try {
      // Use count queries for key tables
      const tables = [
        'analytics_events', 'bookings', 'booking_status_history', 'clients',
        'practitioners', 'email_logs', 'email_campaigns', 'messages',
        'reviews', 'services', 'session_transcripts', 'user_sessions',
        'ai_navigator_conversations', 'ai_token_usage', 'conversations',
        'job_openings', 'job_applications', 'group_events',
      ];

      const stats: TableStat[] = [];
      for (const tableName of tables) {
        try {
          const { count, error } = await (supabase.from(tableName as any).select('*', { count: 'exact', head: true }));
          if (!error) {
            stats.push({
              table_name: tableName,
              row_count: count || 0,
              total_size: '-',
              size_bytes: 0,
            });
          }
        } catch {
          // Skip tables that error
        }
      }

      stats.sort((a, b) => b.row_count - a.row_count);
      setTableStats(stats);
    } catch (err) {
      console.error('Failed to fetch table stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunCleanup = async () => {
    setCleanupRunning(true);
    setCleanupResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.rpc('cleanup_stale_operational_data');
      if (error) throw error;
      setCleanupResult(data as Record<string, number>);
      // Log the cleanup action
      if (session?.user?.id) {
        logDataProcessing({
          actorId: session.user.id,
          actorType: 'admin',
          action: 'delete',
          resourceType: 'operational_data',
          details: `Admin triggered data cleanup: ${JSON.stringify(data)}`,
        });
      }
      toast({
        title: t('dataManagement.cleanupSuccess', 'Cleanup Completed'),
        description: t('dataManagement.cleanupSuccessDesc', 'Stale operational data has been cleaned up.'),
      });
      fetchTableStats();
    } catch (err: any) {
      toast({
        title: t('dataManagement.cleanupFailed', 'Cleanup Failed'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setCleanupRunning(false);
    }
  };

  const handleExport = async () => {
    if (!exportTable) return;
    setExporting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const params = new URLSearchParams({ action: 'export', table: exportTable });
      if (exportStartDate) params.set('start_date', exportStartDate);
      if (exportEndDate) params.set('end_date', exportEndDate);

      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/admin-data-export?${params}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${exportTable}_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: t('dataManagement.exportSuccess', 'Export Complete'),
        description: t('dataManagement.exportSuccessDesc', 'CSV file has been downloaded.'),
      });
      // Log the export action
      if (session?.user?.id) {
        logDataProcessing({
          actorId: session.user.id,
          actorType: 'admin',
          action: 'export',
          resourceType: exportTable,
          details: `Admin exported ${exportTable} (${exportStartDate || 'all'} to ${exportEndDate || 'all'})`,
        });
      }
    } catch (err: any) {
      toast({
        title: t('dataManagement.exportFailed', 'Export Failed'),
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  const formatRowCount = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  const getRowCountBadge = (count: number) => {
    if (count > 100000) return 'destructive';
    if (count > 10000) return 'secondary';
    return 'outline';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Database className="h-6 w-6" />
          {t('dataManagement.title', 'Data Management')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('dataManagement.description', 'Monitor data usage, manage retention policies, and export data for compliance.')}
        </p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <BarChart3 className="h-4 w-4 mr-1" />
            {t('dataManagement.overview', 'Overview')}
          </TabsTrigger>
          <TabsTrigger value="retention">
            <Shield className="h-4 w-4 mr-1" />
            {t('dataManagement.retention', 'Retention Policies')}
          </TabsTrigger>
          <TabsTrigger value="cleanup">
            <Trash2 className="h-4 w-4 mr-1" />
            {t('dataManagement.cleanup', 'Data Cleanup')}
          </TabsTrigger>
          <TabsTrigger value="export">
            <Download className="h-4 w-4 mr-1" />
            {t('dataManagement.export', 'Data Export')}
          </TabsTrigger>
          <TabsTrigger value="dsar">
            <FileText className="h-4 w-4 mr-1" />
            {t('dataManagement.dsar.tab', 'DSAR Requests')}
          </TabsTrigger>
          <TabsTrigger value="quality">
            <Activity className="h-4 w-4 mr-1" />
            {t('dataManagement.dataQuality.tab', 'Data Quality')}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{t('dataManagement.tableOverview', 'Table Size Overview')}</CardTitle>
                <CardDescription>
                  {t('dataManagement.tableOverviewDesc', 'Row counts for all tracked tables')}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={fetchTableStats} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                {t('dataManagement.refresh', 'Refresh')}
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dataManagement.tableName', 'Table')}</TableHead>
                      <TableHead className="text-right">{t('dataManagement.rowCount', 'Rows')}</TableHead>
                      <TableHead>{t('dataManagement.status', 'Status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableStats.map((stat) => {
                      const policy = RETENTION_POLICIES.find(p => p.table === stat.table_name);
                      return (
                        <TableRow key={stat.table_name}>
                          <TableCell className="font-mono text-sm">{stat.table_name}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant={getRowCountBadge(stat.row_count) as any}>
                              {formatRowCount(stat.row_count)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {policy?.autoCleanup ? (
                              <Badge variant="default">
                                <Clock className="h-3 w-3 mr-1" />
                                {t('dataManagement.autoCleanup', 'Auto-cleanup')}
                              </Badge>
                            ) : policy ? (
                              <Badge variant="outline">
                                <Shield className="h-3 w-3 mr-1" />
                                {policy.retention}
                              </Badge>
                            ) : (
                              <Badge variant="outline">{t('dataManagement.unmanaged', 'Unmanaged')}</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retention Policies Tab */}
        <TabsContent value="retention">
          <Card>
            <CardHeader>
              <CardTitle>{t('dataManagement.retentionTitle', 'Data Retention Policies')}</CardTitle>
              <CardDescription>
                {t('dataManagement.retentionDesc', 'Legal retention requirements and auto-cleanup schedules')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dataManagement.tableName', 'Table')}</TableHead>
                    <TableHead>{t('dataManagement.retentionPeriod', 'Retention')}</TableHead>
                    <TableHead>{t('dataManagement.legalBasis', 'Legal Basis')}</TableHead>
                    <TableHead>{t('dataManagement.category', 'Category')}</TableHead>
                    <TableHead>{t('dataManagement.autoCleanupHeader', 'Auto-Cleanup')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {RETENTION_POLICIES.map((policy) => (
                    <TableRow key={policy.table}>
                      <TableCell className="font-mono text-sm">{policy.table}</TableCell>
                      <TableCell>{policy.retention}</TableCell>
                      <TableCell>{policy.legal}</TableCell>
                      <TableCell>
                        <Badge variant={
                          policy.category === 'clinical' ? 'destructive' :
                          policy.category === 'operational' ? 'default' :
                          policy.category === 'user-managed' ? 'secondary' : 'outline'
                        }>
                          {policy.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {policy.autoCleanup ? (
                          <Badge variant="default">✓ Active</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cleanup Tab */}
        <TabsContent value="cleanup">
          <Card>
            <CardHeader>
              <CardTitle>{t('dataManagement.cleanupTitle', 'Manual Data Cleanup')}</CardTitle>
              <CardDescription>
                {t('dataManagement.cleanupDesc', 'Run the scheduled cleanup manually. This deletes: analytics_events (>90d), email_logs (>180d), user_sessions (>30d), system_message_dismissals (>90d).')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={cleanupRunning}>
                      {cleanupRunning ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 mr-2" />
                      )}
                      {t('dataManagement.runCleanupNow', 'Run Cleanup Now')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t('dataManagement.cleanupConfirmTitle', 'Run Data Cleanup?')}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('dataManagement.cleanupConfirmDesc', 'This will permanently delete stale operational data according to retention policies. Clinical and account data will NOT be affected.')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('cancel', 'Cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRunCleanup}>
                        {t('dataManagement.confirmCleanup', 'Yes, Run Cleanup')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <p className="text-sm text-muted-foreground">
                  {t('dataManagement.cronNote', 'Auto-cleanup runs daily at 3:00 AM UTC')}
                </p>
              </div>

              {cleanupResult && (
                <Card className="bg-muted/50">
                  <CardContent className="pt-4">
                    <h4 className="font-medium mb-2">{t('dataManagement.cleanupResults', 'Cleanup Results')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(cleanupResult).filter(([k]) => k !== 'executed_at').map(([key, value]) => (
                        <div key={key} className="text-center">
                          <div className="text-2xl font-bold">{value}</div>
                          <div className="text-xs text-muted-foreground">
                            {key.replace(/_/g, ' ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export">
          <Card>
            <CardHeader>
              <CardTitle>{t('dataManagement.exportTitle', 'Data Export')}</CardTitle>
              <CardDescription>
                {t('dataManagement.exportDesc', 'Export table data as CSV for audits and compliance.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('dataManagement.selectTable', 'Select Table')}</Label>
                  <Select value={exportTable} onValueChange={setExportTable}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('dataManagement.chooseTables', 'Choose a table...')} />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPORTABLE_TABLES.map((table) => (
                        <SelectItem key={table} value={table}>
                          {table}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('dataManagement.startDate', 'Start Date (optional)')}</Label>
                  <Input
                    type="date"
                    value={exportStartDate}
                    onChange={(e) => setExportStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('dataManagement.endDate', 'End Date (optional)')}</Label>
                  <Input
                    type="date"
                    value={exportEndDate}
                    onChange={(e) => setExportEndDate(e.target.value)}
                  />
                </div>
              </div>

              <Button onClick={handleExport} disabled={!exportTable || exporting}>
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {t('dataManagement.exportCsv', 'Export as CSV')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        {/* DSAR Tab */}
        <TabsContent value="dsar">
          <AdminDsarTab />
        </TabsContent>

        {/* Data Quality Tab */}
        <TabsContent value="quality">
          <AdminDataQualityTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDataManagement;
